import { Injectable } from '@angular/core';

import OrderAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity';
import SubRouteAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route.entity';

import type { ReviewOrderDelta } from 'app/modules/components/routing/sub-routes/route-optimization/confirm-review';
import { Dash, OfficePlanView } from 'app/modules/components/routing/sub-routes/route-optimization/plan-result';
import { RouteMapFeatureCollection } from 'app/modules/components/routing/sub-routes/route-optimization/route-optimization.interface';
import { RouteTag } from 'app/modules/components/routing/sub-routes/route-optimization/route-tags';
import { RunAverages } from 'app/modules/components/routing/sub-routes/route-optimization/route-times';

// Tipos del diálogo, **solo como tipos**: `import type` se borra al compilar, así
// que el diálogo puede importar este servicio sin que quede un ciclo en runtime.
import type {
  DialogState,
  RouteOptimizationSuggestionData,
  ScopeCounts,
  ScopeSelection,
  ViewMode,
} from './route-optimization-suggestion.dialog';

/**
 * Guarda la última corrida del optimizador para que cerrar el modal no la tire.
 *
 * El diálogo se abre con `dialog.open()`, que construye un componente nuevo cada
 * vez: su estado moría con él y una corrida son minutos de cálculo (un `/plan`
 * por sub-ruta). Acá vive entre aperturas.
 *
 * **En memoria, a propósito.** Nada va a `localStorage`: la corrida arrastra las
 * órdenes completas —262 documentos de Mongo con sus líneas de material— más la
 * geometría decodificada de cada ruta, que revienta la cuota de 5 MB con
 * comodidad y obligaría a serializar `Map`, `Set` y `Date` a mano. Además dejaría
 * pedidos de producción escritos en disco, que no es algo que se decida de paso.
 * El costo es que un F5 la pierde, y eso es coherente: después de recargar, la
 * probabilidad de que los datos hayan cambiado es todavía mayor.
 *
 * **La foto se guarda por referencia, no copiada.** Hay un solo consumidor a la
 * vez —el diálogo abierto— así que copiar 262 órdenes en profundidad sería
 * desperdicio. El efecto lateral es deseable: lo que el diálogo va tocando
 * (tarjetas abiertas, detalles) sigue ahí al reabrir.
 */

/** Lo que el usuario eligió para correr. Sobrevive incluso sin resultado. */
export interface RunInputs {
  selectedOfficeIds: number[];
  /** Territorios de la corrida (ids numéricos). Todos elegidos = sin filtro. */
  selectedTerritoryIds: number[];
  /** Camiones disponibles de la corrida (`vehicle._id`). */
  selectedVehicleIds: string[];
  selectedServiceMin: number;
  shiftStart: string;
  shiftEnd: string;
  scopeSelection: ScopeSelection;
  /** Viajes máximos por camión (1 = sin recargas). */
  maxTrips: number;
  /** Minutos de recarga entre viajes. */
  reloadMin: number;
  /**
   * Precio del combustible en lempiras por galon, o `null` si no se cargo.
   *
   * Opcional: una foto guardada antes de que existiera el KPI de combustible no lo
   * trae, y restaurarla sin precio es exactamente lo que ese usuario estaba viendo.
   */
  fuelPricePerGallon?: number | null;
  /** Rendimiento de la flota en km por galon, o `null` si no se cargo. */
  kmPerGallon?: number | null;
}

/**
 * La corrida terminada.
 *
 * Solo se guarda **completa**: si el modal se cierra mientras optimiza, las
 * promesas siguen corriendo contra un componente destruido y sus escrituras van a
 * la nada, así que una foto a medias describiría un plan que nunca existió.
 */
export interface RunResult {
  state: DialogState;
  errorMessage: string;
  loadedScopeLabel: string;
  officesWithoutGeo: string[];
  scopeCounts: ScopeCounts;
  officePlans: OfficePlanView[];
  dash?: Dash;
  routeOptions: Array<{ truckId: string; label: string; color: string; stops: number }>;
  geojsonByGroup: Map<string, RouteMapFeatureCollection>;
  allOrders: OrderAttributes[];
  weightByCustomer: Map<string, number>;
  weightByOrderId: Map<string, number>;
  subRoutesById: Map<string, SubRouteAttributes>;
  truckColorIndex: number;
  routeTags: RouteTag[];
  /** Baseline del KPI "tiempo ahorrado", por `groupKey` (orden sin optimizar). */
  baselineByGroupKey: Map<string, { durationMin: number; distanceKm: number }>;
  averages: RunAverages;
  /** Atención y jornada con las que se calculó ESTO, no las que muestra el control. */
  assumedServiceMin: number;
  assumedShift: { start: string; end: string };
  groupsDone: number;
  groupsTotal: number;
}

/**
 * Lo que el operador **decidió** en el gestor de confirmación.
 *
 * Aparte del resto de la foto porque no es "dónde estaba mirando": es trabajo. Y
 * viaja como DELTA y no como las rutas enteras — dos representaciones del mismo
 * orden divergen, y acá divergir significa mostrar una y confirmar la otra. El
 * plan (`RunResult.officePlans`) sigue siendo la única fuente de las rutas.
 */
export interface ReviewViewState {
  /**
   * Por sub-ruta, la secuencia de `orderId` que el operador dejó. Solo las rutas
   * que tocó: una ausente significa "quedó como la propuso el motor".
   */
  order: ReviewOrderDelta;
  /**
   * Sub-rutas donde adoptó el orden de visita guardado del viaje.
   *
   * `Array` y no `Set`, igual que `selectedTagKeys`: la foto es un dato. Sin esto,
   * al reabrir el aviso de pisada vuelve a dispararse donde ya no es cierto —lo
   * que se va a guardar ES ese orden— y ese aviso es el único que protege un daño
   * invisible.
   */
  appliedSaved: string[];
  /** Si el gestor estaba abierto al cerrar el diálogo. */
  open: boolean;
  /** La pestaña vigente del gestor. */
  tab: 'optimizer' | 'custom';
}

/** Dónde estaba mirando el usuario. */
export interface ViewState {
  activeTab: 'list' | 'map';
  selectedTruckId: string | null;
  searchText: string;
  /** `Array` y no `Set`: la foto es un dato, no el estado vivo del diálogo. */
  selectedTagKeys: string[];
  /**
   * Modo de presentación de las rutas propuestas.
   *
   * Opcional porque el campo llegó después del resto de la foto, no porque el
   * diálogo pueda dejar de escribirlo: lo guarda siempre. Y esta caché vive en
   * MEMORIA, así que una foto no sobrevive a un deploy — una sin este campo no
   * puede existir en la práctica. Ausente, el diálogo aplica `DEFAULT_VIEW_MODE`:
   * sin campo no hay elección del usuario que respetar.
   */
  viewMode?: ViewMode;
  /**
   * La ruta elegida en el mosaico del modo grid.
   *
   * Distinto de `selectedTruckId`, que es la del MAPA: el mapa abre en la primera
   * ruta por requisito, y compartir el campo lo romperia.
   */
  gridTruckId?: string | null;
  /**
   * Ancho del mosaico en pixeles, o `null` si el usuario nunca lo movio.
   *
   * `null` y no el valor por omision: guardar el default lo volveria
   * indistinguible de una eleccion deliberada, y el dia que el default cambie las
   * fotos viejas quedarian clavadas al viejo.
   */
  gridTilesWidth?: number | null;
  /**
   * La ruta elegida en el mosaico del gestor de confirmacion, por `subRouteId`.
   *
   * Aparte de `gridTruckId`: ese es del resultado y su identidad es el `truckId`.
   * El MODO se comparte entre los dos pasos; la seleccion no puede.
   */
  reviewGridRouteId?: string | null;
  /**
   * Lo editado en el gestor. Ver `ReviewViewState`.
   *
   * Opcional: una foto guardada antes de que esto se conservara no lo trae, y
   * restaurar sin orden editado es exactamente lo que ese usuario tenía. Ausente
   * también cuando no hubo revisión: sin rutas en el gestor no hay decisión que
   * guardar.
   */
  review?: ReviewViewState;
}

/** Una corrida guardada, con la huella de las entradas que la produjeron. */
export interface RunSnapshot {
  fingerprint: string;
  /** Momento en que se guardó, para poder declarar la antigüedad del plan. */
  savedAt: number;
  inputs: RunInputs;
  view: ViewState;
  result?: RunResult;
}

/**
 * Huella de las entradas que la pantalla le pasa al modal.
 *
 * El plan es una **foto de un cálculo sobre datos que se mueven**: entre cerrar y
 * reabrir, el usuario puede cambiar el día de carga, la compañía o los filtros de
 * oficina/ruta/sub-ruta —y asignar pedidos a viajes, que es a lo que se dedica
 * esta pantalla—. Restaurar un plan calculado con otras entradas invitaría a
 * despachar camiones contra pedidos que ya se movieron.
 *
 * Los ids se **ordenan** antes de unirse: `[4, 7]` y `[7, 4]` son el mismo filtro
 * y no deben producir huellas distintas.
 *
 * `UMPActive` queda afuera a propósito: solo cambia cómo se rotulan las cantidades
 * en la tabla de materiales, no el plan. Y cuando cambia, cambió la compañía, que
 * ya está en la huella.
 *
 * Función pura: entra el contrato de entrada, sale una cadena.
 */
export function runFingerprint(data: RouteOptimizationSuggestionData): string {
  const ids = (list?: number[]): string => [...(list ?? [])].sort((a, b) => a - b).join(',');
  return [
    data?.companyMongoId ?? '',
    data?.companyId ?? '',
    // El rango UTC del día ES el día de carga; se incluye la fecha igual porque es
    // barata y hace la huella legible al depurar.
    toIso(data?.loadDate),
    data?.dayRange?.start ?? '',
    data?.dayRange?.end ?? '',
    ids(data?.salesOfficeIds),
    ids(data?.routeIds),
    ids(data?.subRouteIds),
    ids(data?.customerIds),
    // Códigos de texto: mismo tratamiento que los ids — orden estable.
    [...(data?.documentClassCodes ?? [])].sort().join(','),
  ].join('|');
}

/** ISO de una fecha que puede llegar como `Date` o como cadena. */
function toIso(value: Date | string | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

@Injectable({ providedIn: 'root' })
export class RouteOptimizationRunCache {
  private snapshot: RunSnapshot | null = null;

  /**
   * Guarda la foto. `savedAt` lo pone el llamador para no atar el servicio al
   * reloj: así el test fija el instante en vez de tolerar cualquiera.
   */
  save(snapshot: RunSnapshot): void {
    this.snapshot = snapshot;
  }

  /**
   * La foto guardada, sin juzgarla.
   *
   * La comparación de huellas la hace el diálogo y no este servicio porque la
   * respuesta **no es binaria**: con la huella distinta el plan se descarta, pero
   * las preferencias del usuario —atención por parada, jornada— siguen siendo
   * suyas y valen para cualquier día. Devolver `null` de golpe se las llevaría
   * puestas.
   *
   * Y que la huella coincida **no garantiza** que el plan siga vigente: alguien
   * puede haber asignado pedidos a un viaje sin tocar ningún filtro, y eso no se
   * detecta sin volver a consultar. Por eso la foto lleva `savedAt` y la pantalla
   * declara cuándo se calculó, en vez de fingir que está fresca.
   */
  read(): RunSnapshot | null {
    return this.snapshot;
  }

  /** Descarta la foto. Se usa al volver a calcular desde cero. */
  clear(): void {
    this.snapshot = null;
  }
}
